import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { StyleSpecification } from "maplibre-gl";

import { MAP_STYLES } from "@/constants/map";
import { MapProvider, MapSource, SelectedFeature } from "@/types/map";

type MapStyleKey = keyof typeof MAP_STYLES;

export type LegendFilterType =
  | string
  | { min: number | null; max: number | null }
  | null;
export interface MapState {
  selectedFeature: SelectedFeature | null;
  comparisonFeatureData: Array<SelectedFeature>;
  draw: {
    popupInfo: {
      lngLat: [number, number];
    } | null;
    feature: GeoJSON.Feature | null;
  };
  legendFilters?: { [datasetId: string]: LegendFilterType[] };
  baseMap: {
    title: string;
    description: string;
    value: StyleSpecification | string;
    provider: MapProvider;
    thumbnail?: string;
  };
  mapSource: MapSource;
}

export const mapInitialState: MapState = {
  selectedFeature: null,
  comparisonFeatureData: [],
  draw: {
    popupInfo: null,
    feature: null
  },
  legendFilters: undefined,
  baseMap: MAP_STYLES.street,
  mapSource: MapSource.STYLE_URL
};

export const mapSlice = createSlice({
  name: "map",
  initialState: mapInitialState,
  reducers: {
    setSelectedFeature: (
      state,
      action: PayloadAction<SelectedFeature | null>
    ) => {
      state.selectedFeature = action.payload;
    },
    setComparisonFeature: (
      state,
      action: PayloadAction<Array<SelectedFeature>>
    ) => {
      state.comparisonFeatureData = action.payload;
    },
    setLegendFilters: (
      state,
      action: PayloadAction<
        { [datasetId: string]: LegendFilterType[] } | undefined
      >
    ) => {
      state.legendFilters = action.payload;
    },
    removeComparisonFeatureByIndex: (state, action: PayloadAction<number>) => {
      state.comparisonFeatureData.splice(action.payload, 1);
    },
    clearComparisonFeature: (state) => {
      state.comparisonFeatureData = [];
    },
    addComparisonFeature: (state, action: PayloadAction<SelectedFeature>) => {
      state.comparisonFeatureData = [
        ...state.comparisonFeatureData,
        action.payload
      ];
    },
    setMapStyle: (state, action: PayloadAction<MapStyleKey>) => {
      const selectedBaseMap = MAP_STYLES[action.payload];

      // @ts-expect-error warning not clear
      state.baseMap = selectedBaseMap;

      if (selectedBaseMap.provider === MapProvider.MAPBOX)
        state.mapSource = MapSource.MAPBOX;
      else state.mapSource = MapSource.STYLE_URL;
    },
    setMapSource: (
      state,
      action: PayloadAction<MapSource.MAPBOX | MapSource.STYLE_URL>
    ) => {
      state.mapSource = action.payload;
    },
    resetMapState: () => ({ ...mapInitialState })
  }
});

export const {
  setSelectedFeature,
  setComparisonFeature,
  setLegendFilters,
  addComparisonFeature,
  clearComparisonFeature,
  removeComparisonFeatureByIndex,
  setMapStyle,
  resetMapState
} = mapSlice.actions;

export const mapReducer = mapSlice.reducer;
